/**
 * useTrsTimeEntries
 *
 * TRS's `time_entries` table, read via `GET /post-times`. Field readers
 * below are informed by TRS's OWN confirmed data model (from working
 * directly on that project) rather than pure guessing:
 *   - `hours` (0 for expense rows) and a SEPARATE `amount` field
 *     (decimal, expense $) are both real, confirmed columns.
 *   - `approval_status` (pending/approved/rejected) is the REAL,
 *     actively-enforced approval mechanism (`TimeEntryApprovalController`)
 *     -- NOT the `wip\ActionsController`'s `/get-approved`/
 *     `/get-trs-status` routes an earlier pass in this project guessed
 *     at. Those two routes are NEVER mentioned anywhere in TRS's own
 *     extensive documentation despite it covering the approval workflow
 *     in detail -- real risk they're incomplete/unmaintained scaffolding
 *     rather than the intended integration point. Switched away from
 *     them for that reason.
 *   - `job_type` (`client`/`office`/`none-client`/`expense-client`/
 *     `expense-office`) distinguishes real chargeable client time from
 *     internal/office/expense entries -- Office Hour entries have no
 *     Engagement at all per TRS's own UI, so filtering to `job_type ===
 *     'client'` (plus a non-null engagement id) is what
 *     `isChargeableClientEntry()` does before any WIP figure counts an
 *     entry's hours.
 *   - `client_name`/`engagement_title` are DENORMALIZED STRING SNAPSHOTS
 *     saved at entry-creation time (TRS has no local clients/engagements
 *     mirror table) -- available as a display fallback, but the
 *     numeric `engagement_id` itself is what's used to join back to
 *     SSS's real engagement records.
 *
 * ⚠️ Still NOT confirmed: `/post-times`' exact query parameter names
 * (`date_from`/`date_to`/`engagement_id` are a guess, same caveat
 * `/schedules` had before its real `start_date`/`end_date`/`branch_id[]`
 * contract got confirmed), and the exact field names for `user_id` and
 * `engagement_id` themselves (read defensively, trying `staff_id` too).
 */
import { useQuery } from '@tanstack/react-query';
import { trsApiClient } from '../services/apiClient.js';

function unwrapList(data) {
  return Array.isArray(data) ? data : (data?.data ?? []);
}

/**
 * @param {{ engagementId?: string|null, dateFrom?: string, dateTo?: string, enabled?: boolean }} [filters]
 */
export const useTimeEntries = (filters = {}) => {
  const today = new Date();
  const fallbackStart = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
  const fallbackEnd = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
  const dateFrom = filters.dateFrom ?? fallbackStart.toISOString().slice(0, 10);
  const dateTo = filters.dateTo ?? fallbackEnd.toISOString().slice(0, 10);

  return useQuery({
    queryKey: ['trs', 'post-times', filters.engagementId, dateFrom, dateTo],
    queryFn: async ({ signal }) => {
      const { data } = await trsApiClient.get('/post-times', {
        signal,
        params: {
          engagement_id: filters.engagementId ?? undefined,
          date_from: dateFrom,
          date_to: dateTo,
        },
      });
      return unwrapList(data);
    },
    enabled: filters.enabled ?? true,
    staleTime: 30_000,
  });
};

/** @param {any} entry */
export function timeEntryUserId(entry) {
  return String(entry.user_id ?? entry.staff_id ?? '');
}
/** @param {any} entry */
export function timeEntryEngagementId(entry) {
  return entry.engagement_id != null ? String(entry.engagement_id) : null;
}
/** CONFIRMED real field name. @param {any} entry */
export function timeEntryHours(entry) {
  return Number(entry.hours ?? 0);
}
/** CONFIRMED real field name (expense $, separate from hours). @param {any} entry */
export function timeEntryAmount(entry) {
  return Number(entry.amount ?? 0);
}
/** CONFIRMED real enum field. @param {any} entry */
export function timeEntryJobType(entry) {
  return entry.job_type ?? null;
}
/** CONFIRMED real field name -- the actively-enforced approval mechanism. @param {any} entry */
export function timeEntryApprovalStatus(entry) {
  return entry.approval_status ?? null;
}
/** @param {any} entry */
export function timeEntryDate(entry) {
  return entry.date ?? null;
}
/** Denormalized snapshot, display fallback only -- join via timeEntryEngagementId() instead. @param {any} entry */
export function timeEntryClientName(entry) {
  return entry.client_name ?? null;
}
/** Denormalized snapshot, display fallback only. @param {any} entry */
export function timeEntryEngagementTitle(entry) {
  return entry.engagement_title ?? null;
}
/** @param {any} entry */
export function timeEntryStaffName(entry) {
  return entry.staff_name ?? entry.user_name ?? entry.name ?? null;
}

/**
 * True for real, hour-based, engagement-attributable client time --
 * excludes office/none-client/expense-* entries (which either have no
 * engagement at all, or track a dollar `amount` rather than `hours`).
 * Every WIP figure that sums TRS hours filters through this first.
 * @param {any} entry
 */
export function isChargeableClientEntry(entry) {
  return timeEntryJobType(entry) === 'client' && timeEntryEngagementId(entry) != null;
}
/** @param {any} entry */
export function isApprovedEntry(entry) {
  return timeEntryApprovalStatus(entry) === 'approved';
}
/** @param {any} entry */
export function isPendingEntry(entry) {
  return timeEntryApprovalStatus(entry) === 'pending';
}
/** @param {any} entry */
export function isRejectedEntry(entry) {
  return timeEntryApprovalStatus(entry) === 'rejected';
}
